export interface ToolbarButton {
    command?: string;
    label?: string;
    title?: string;
    className?: string;
}

export const createSeparator = (): HTMLDivElement => {
    const separator = document.createElement('div');
    separator.style.cssText = `
      width: 1px;
      height: 24px;
      background: #ddd;
      margin: 0 6px;
    `;
    return separator;
};

export const createButton = (
    btn: ToolbarButton,
    contentRef: React.RefObject<HTMLDivElement>
): HTMLButtonElement => {
    const button = document.createElement('button');
    button.innerHTML = btn.label || '';
    button.title = btn.title || '';
    button.className = btn.className || '';
    button.setAttribute('data-command', btn.command || '');
    button.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 4px;
      `;

    // Function to update button active state
    const updateActiveState = () => {
        let isActive = false;

        if (btn.command) {
            // Special handling for subscript and superscript since queryCommandState is unreliable for them
            if (btn.command === 'subscript' || btn.command === 'superscript') {
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    let currentNode: Node | null = range.startContainer;

                    // Walk up the DOM tree to check for sub/sup elements
                    while (currentNode && currentNode !== contentRef.current) {
                        if (currentNode.nodeType === Node.ELEMENT_NODE) {
                            const element = currentNode as Element;
                            const tagName = element.tagName.toLowerCase();
                            if (
                                (btn.command === 'subscript' && tagName === 'sub') ||
                                (btn.command === 'superscript' && tagName === 'sup')
                            ) {
                                isActive = true;
                                break;
                            }
                        }
                        currentNode = currentNode.parentNode;
                    }
                }
            } else {
                // Use queryCommandState for other commands
                isActive = document.queryCommandState(btn.command);
            }
        }

        if (isActive) {
            button.style.backgroundColor = '#e0e0e0';
            button.style.borderColor = '#000';
            button.style.color = '#000';
            button.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.2)';
        } else {
            button.style.backgroundColor = 'white';
            button.style.borderColor = '#ddd';
            button.style.color = 'inherit';
            button.style.boxShadow = 'none';
        }
    };

    // Store the update function on the button for external access
    (button as any).updateActiveState = updateActiveState;

    button.addEventListener('mouseenter', () => {
        let isCurrentlyActive = false;

        if (btn.command) {
            // Use the same logic as updateActiveState to check if button is currently active
            if (btn.command === 'subscript' || btn.command === 'superscript') {
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    let currentNode: Node | null = range.startContainer;

                    while (currentNode && currentNode !== contentRef.current) {
                        if (currentNode.nodeType === Node.ELEMENT_NODE) {
                            const element = currentNode as Element;
                            const tagName = element.tagName.toLowerCase();
                            if (
                                (btn.command === 'subscript' && tagName === 'sub') ||
                                (btn.command === 'superscript' && tagName === 'sup')
                            ) {
                                isCurrentlyActive = true;
                                break;
                            }
                        }
                        currentNode = currentNode.parentNode;
                    }
                }
            } else {
                isCurrentlyActive = document.queryCommandState(btn.command);
            }
        }

        if (!isCurrentlyActive) {
            button.style.backgroundColor = '#f8f9fa';
            button.style.borderColor = '#007cba';
        }
    });

    button.addEventListener('mouseleave', () => {
        updateActiveState(); // Reset to active state if applicable
    });

    button.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (btn.command && contentRef.current) {
            // Execute command immediately on mousedown to preserve selection
            document.execCommand(btn.command, false);
        }
    });

    return button;
};

export const createHeadingSelect = (
    contentRef: React.RefObject<HTMLDivElement>
): HTMLSelectElement => {
    const select = document.createElement('select');
    select.style.cssText = `
        padding: 6px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        font-size: 14px;
        min-width: 100px;
    `;

    // Add a disabled default option first
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Format';
    defaultOption.disabled = true;
    defaultOption.selected = true;
    select.appendChild(defaultOption);

    const options = [
        { value: 'div', text: 'Normal' },
        { value: 'h1', text: 'Heading 1' },
        { value: 'h2', text: 'Heading 2' },
        { value: 'h3', text: 'Heading 3' },
        { value: 'h4', text: 'Heading 4' },
        { value: 'h5', text: 'Heading 5' },
        { value: 'h6', text: 'Heading 6' },
    ];

    options.forEach((option) => {
        const optionElement = document.createElement('option');
        optionElement.value = option.value;
        optionElement.textContent = option.text;
        select.appendChild(optionElement);
    });

    select.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (select.value && contentRef.current) {
            // Focus back to the editor to maintain the selection
            contentRef.current.focus();

            try {
                document.execCommand('formatBlock', false, select.value);
                // Reset dropdown to default state after applying format
                setTimeout(() => {
                    select.value = '';
                }, 100);
            } catch (error) {
                // Format block failed
                select.value = ''; // Reset on error too
            }
        }
    });

    return select;
};

export const createFontFamilySelect = (
    allFonts: Array<{ name: string } | string>,
    contentRef: React.RefObject<HTMLDivElement>
): HTMLSelectElement => {
    const select = document.createElement('select');
    select.style.cssText = `
        padding: 6px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        font-size: 14px;
        min-width: 140px;
    `;

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Font Family';
    defaultOption.disabled = true;
    defaultOption.selected = true;
    select.appendChild(defaultOption);

    // Add theme fonts
    allFonts.forEach((font) => {
        const option = document.createElement('option');
        const fontName = typeof font === 'string' ? font : font.name || '';
        option.value = fontName;
        option.textContent = fontName;
        option.style.fontFamily = fontName;
        select.appendChild(option);
    });

    select.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (select.value && contentRef.current) {
            // Focus back to the editor to maintain the selection
            contentRef.current.focus();

            // Use execCommand for reliability, transformation will happen on save
            try {
                const success = document.execCommand('fontName', false, select.value);
                if (!success) {
                    // Fallback: try applying style directly
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        if (!range.collapsed) {
                            const span = document.createElement('span');
                            span.style.fontFamily = select.value;
                            range.surroundContents(span);
                        }
                    }
                }
                // Reset dropdown to default state after applying font
                setTimeout(() => {
                    select.value = '';
                }, 100);
            } catch (error) {
                // Font application failed
                select.value = ''; // Reset on error too
            }
        }
    });

    return select;
};

export const createFontSizeSelect = (
    contentRef: React.RefObject<HTMLDivElement>
): HTMLSelectElement => {
    const select = document.createElement('select');
    select.style.cssText = `
        padding: 6px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        font-size: 14px;
        min-width: 80px;
    `;

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Size';
    defaultOption.disabled = true;
    defaultOption.selected = true;
    select.appendChild(defaultOption);

    const sizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
    sizes.forEach((size) => {
        const option = document.createElement('option');
        option.value = size.toString();
        option.textContent = `${size}px`;
        select.appendChild(option);
    });

    select.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (select.value && contentRef.current) {
            // Focus the editor first to ensure selection is active
            contentRef.current.focus();

            // Get current selection
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const selectedText = selection.toString();

                if (selectedText) {
                    try {
                        // Try execCommand first for better multi-line support
                        const success = document.execCommand('fontSize', false, '7'); // Use size 7 as base

                        if (success) {
                            // Find all font elements created by execCommand and replace with our styling
                            const fontElements =
                                contentRef.current.querySelectorAll('font[size="7"]');
                            fontElements.forEach((fontElement) => {
                                const span = document.createElement('span');
                                span.style.fontSize = `${select.value}px`;

                                // Copy all child nodes
                                while (fontElement.firstChild) {
                                    span.appendChild(fontElement.firstChild);
                                }

                                // Replace font element with span
                                fontElement.parentNode?.replaceChild(span, fontElement);
                            });

                            // Restore selection
                            const newRange = document.createRange();
                            newRange.selectNodeContents(contentRef.current);
                            const walker = document.createTreeWalker(
                                contentRef.current,
                                NodeFilter.SHOW_TEXT,
                                null
                            );

                            let startFound = false;
                            let startNode: Node | null = null;
                            let endNode: Node | null = null;
                            let startOffset = 0;
                            let endOffset = 0;

                            // Find the text nodes that were part of our selection
                            while (walker.nextNode()) {
                                const textNode = walker.currentNode;
                                const nodeText = textNode.textContent || '';

                                if (
                                    !startFound &&
                                    selectedText.includes(
                                        nodeText.substring(0, Math.min(10, nodeText.length))
                                    )
                                ) {
                                    startNode = textNode;
                                    startOffset = 0;
                                    startFound = true;
                                }

                                if (
                                    startFound &&
                                    selectedText.includes(
                                        nodeText.substring(Math.max(0, nodeText.length - 10))
                                    )
                                ) {
                                    endNode = textNode;
                                    endOffset = nodeText.length;
                                }
                            }

                            if (startNode && endNode) {
                                newRange.setStart(startNode, startOffset);
                                newRange.setEnd(endNode, endOffset);
                                selection.removeAllRanges();
                                selection.addRange(newRange);
                            }
                        } else {
                            // Fallback to original method for simple selections
                            const span = document.createElement('span');
                            span.style.fontSize = `${select.value}px`;

                            // Check if selection spans multiple block elements
                            const startContainer = range.startContainer;
                            const endContainer = range.endContainer;
                            const isMultiBlock =
                                startContainer !== endContainer &&
                                (startContainer.nodeType === Node.ELEMENT_NODE ||
                                    endContainer.nodeType === Node.ELEMENT_NODE);

                            if (isMultiBlock) {
                                // For multi-block selections, apply formatting to each text node individually
                                const walker = document.createTreeWalker(
                                    range.commonAncestorContainer,
                                    NodeFilter.SHOW_TEXT,
                                    {
                                        acceptNode: (node) => {
                                            return range.intersectsNode(node)
                                                ? NodeFilter.FILTER_ACCEPT
                                                : NodeFilter.FILTER_SKIP;
                                        },
                                    }
                                );

                                const textNodes: Node[] = [];
                                while (walker.nextNode()) {
                                    textNodes.push(walker.currentNode);
                                }

                                textNodes.forEach((textNode) => {
                                    const span = document.createElement('span');
                                    span.style.fontSize = `${select.value}px`;
                                    span.textContent = textNode.textContent;
                                    textNode.parentNode?.replaceChild(span, textNode);
                                });
                            } else {
                                // Single block selection - use original method
                                const contents = range.extractContents();
                                span.appendChild(contents);
                                range.insertNode(span);

                                // Restore selection to highlight the styled text
                                const newRange = document.createRange();
                                newRange.selectNodeContents(span);
                                selection.removeAllRanges();
                                selection.addRange(newRange);
                            }
                        }
                    } catch (error) {
                        // Font size application failed, fallback to simple span wrapping
                        const span = document.createElement('span');
                        span.style.fontSize = `${select.value}px`;

                        try {
                            const contents = range.extractContents();
                            span.appendChild(contents);
                            range.insertNode(span);
                        } catch (e) {
                            // If even this fails, just apply to selection text
                            span.textContent = selectedText;
                            range.deleteContents();
                            range.insertNode(span);
                        }
                    }
                }
            }

            // Trigger toolbar update after a brief delay to ensure DOM is updated
            setTimeout(() => {
                const event = new Event('selectionchange', { bubbles: true });
                document.dispatchEvent(event);
            }, 10);
        }
    });

    return select;
};

export const createLineSpacingSelect = (
    contentRef: React.RefObject<HTMLDivElement>
): HTMLSelectElement => {
    const select = document.createElement('select');
    select.style.cssText = `
        padding: 6px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        font-size: 14px;
        min-width: 120px;
    `;

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Line Spacing';
    defaultOption.disabled = true;
    defaultOption.selected = true;
    select.appendChild(defaultOption);

    const lineHeights = [
        { value: '1', text: '1.0' },
        { value: '1.15', text: '1.15' },
        { value: '1.5', text: '1.5' },
        { value: '2', text: '2.0' },
        { value: '2.5', text: '2.5' },
        { value: '3', text: '3.0' },
    ];

    lineHeights.forEach((lineHeight) => {
        const option = document.createElement('option');
        option.value = lineHeight.value;
        option.textContent = lineHeight.text;
        select.appendChild(option);
    });

    select.addEventListener('change', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (select.value && contentRef.current) {
            // Focus the editor first to ensure selection is active
            contentRef.current.focus();

            // Get current selection
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                // Find the block-level element containing the selection
                let blockElement: Element | null = null;
                let currentNode: Node | null = range.startContainer;

                while (currentNode && currentNode !== contentRef.current) {
                    if (currentNode.nodeType === Node.ELEMENT_NODE) {
                        const element = currentNode as Element;
                        const tagName = element.tagName.toLowerCase();

                        // Check if it's a block-level element
                        if (
                            ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tagName)
                        ) {
                            blockElement = element;
                            break;
                        }
                    }
                    currentNode = currentNode.parentNode;
                }

                // If we found a block element, apply line-height to it
                if (blockElement) {
                    (blockElement as HTMLElement).style.lineHeight = select.value;
                } else {
                    // If no block element found, wrap selection in a div with line-height
                    const selectedText = selection.toString();
                    if (selectedText) {
                        const div = document.createElement('div');
                        div.style.lineHeight = select.value;
                        div.textContent = selectedText;

                        range.deleteContents();
                        range.insertNode(div);

                        // Move cursor after the div
                        range.setStartAfter(div);
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }
            }

            // Reset dropdown to default state after applying line spacing
            setTimeout(() => {
                select.value = '';
            }, 100);
        }
    });

    return select;
};
